import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  Box,
  BulkActionsBar,
  Button,
  DataTableGroupByDropdown,
  DataTableSortByDropdown,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  EmptyState,
  Icon,
  KanbanAddCard,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnSkeleton,
  MenuItem,
  Separator,
  Text,
  ToolbarAction,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsTouchDevice,
  type MenuAnchor,
  type SelectAllGroup,
} from "@salestrak/ui";
import { ViewSearch } from "#app/components/filters/view-search";
import { EntityTag } from "#app/components/entity";
import { FilterPanel } from "#app/components/filters/filter-panel";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { AssigneeFilterChip } from "#app/components/filters/assignee-filter-chip";
import { ASSIGNEE_PRINCIPAL_FIELD } from "#app/lib/filters/build-where";
import { CurrentUserButton } from "#app/components/filters/current-user-button";
import {
  countCardFieldTokens,
  getCardCustomFields,
  getShownCardFieldDefs,
  IssueCard,
  IssueCardProvider,
  useIssueCardEditActions,
  type CardDisplayOptions,
} from "#app/components/issue-card";
import {
  IssueRenameProvider,
  useIssueRenameControls,
} from "#app/components/issues/issue-rename-context";
import {
  CardDisplayFields,
  CardLayoutRows,
  CardSizeRow,
  CollapseEmptyStagesRow,
  countShownCardFields,
  ViewOptionsPanel,
  ViewOptionsTrigger,
  ViewSaveButton,
} from "#app/components/view-options";
import { useAllCustomFields } from "#app/lib/hooks/use-all-custom-fields";
import { useIssueView } from "#app/components/issue-view";
import type { IssueRow } from "#app/lib/data";
import { SelectedRowSync, useBoardSelection } from "#app/hooks/kanban/use-board-selection";
import { IssueMenu } from "#app/components/issue-actions/issue-menu";
import {
  IssueRenameAction,
} from "#app/components/issue-actions/issue-rename-action";
import { useResetOnChange } from "#app/lib/hooks/use-reset-on-change";
import {
  kanbanBoardSubscriptions,
} from "#app/hooks/kanban/kanban-subscriptions";
import {
  useRegisterSubscriptions,
} from "#app/lib/subscriptions/subscription-provider";
import {
  useColumnIssues,
  COLUMN_PAGE_SIZE,
} from "#app/hooks/kanban/use-column-issues";
import { useIssueFilters } from "#app/lib/filters/issue-filter-context";
import {
  scopeWhere,
  scopeWorkflowId,
  scopeKey,
  defaultBoardGrouping,
  type IssueScope,
} from "#app/lib/filters/issue-scope";
import { useWorkflowStagesData } from "#app/lib/hooks/use-kanban-board-data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  useCurrentUserFilterToggle,
} from "#app/lib/hooks/use-current-user-filter-toggle";
import { useStagesDialogActions } from "#app/lib/stages-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import {
  useAutomateFromView,
} from "#app/hooks/automations/use-automate-from-view";
import {
  AutomateStageMenuItem,
} from "#app/components/automations/automate-stage-menu-item";
import {
  StageUpdateCycleMenu,
} from "#app/components/updates/stage-update-cycle-menu";
import { StageGroupChip } from "#app/components/issues/issue-group-title";
import { StageColumnHeader } from "#app/components/issues/stage-column-header";
import {
  useStageEditActions,
} from "#app/components/stages/use-stage-edit-actions";
import { useAppData, type WorkspaceWorkflow as Workflow } from "#app/lib/data";
import { canEdit } from "#app/lib/utils/access-level";
import { combineWhereConditions, buildSearchWhere } from "#app/lib/query-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  AnalyticsView,
  useTrackSearch,
} from "#app/lib/analytics";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { iconSourceToName } from "#app/lib/entity-visuals";
import {
  ISSUE_SORT_ICONS,
  ISSUE_SORT_LABELS,
  buildGroupWhere,
} from "#app/components/issues/issue-grouping";
import { VIEW_PANE_ANCHOR_ID } from "#app/lib/hooks/use-anchor-insets";
import {
  useTrackViewOption,
  useViewConfigState,
  useViewsOptional,
} from "#app/lib/views/view-context";
import { viewArchivedWhere, viewStageWhere } from "#app/lib/view-config";
import {
  useIssuesBoardColumns,
  type Stage,
  type BoardColumn,
  type TeamItem,
  type UserItem,
} from "./use-issues-board-columns";
import {
  useIssuesBoardGrouping,
  BOARD_CARD_SIZE_WIDTHS,
} from "./use-issues-board-grouping";
import { useIssuesBoardDrag } from "./use-issues-board-drag";
import { useIssueBulkActions } from "./use-issue-bulk-actions";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";

// The bulk bar's count-chip noun: "1 Project selected".
const PROJECT_SELECTION_NOUN = { one: "Project", other: "Projects" };

const SEARCH_FIELDS = ["name", "description"];

const getColumnId = (c: BoardColumn) => c.id;
const getColumnLabel = (c: BoardColumn) => c.label;
const getItemId = (i: IssueRow) => i.issueId;
const getItemLabel = (i: IssueRow) => i.name;
const getSortValue = (i: IssueRow) => i.sortOrder;
const isItemDragDisabled = (i: IssueRow) => !i.viewerCanEdit;
// Selection mirrors the drag/table gate: bulk actions only cover editable rows.
const isItemSelectable = (i: IssueRow) => i.viewerCanEdit;
const dragGroupLabel = (n: number) => `${n} Project${n === 1 ? "" : "s"}`;
// Per-card checkboxes only — the board has no select-all, so bulk actions
// always run in ids mode.
const NO_SELECT_ALL_GROUPS: SelectAllGroup[] = [];

/**
 * The entity chip for a column header, resolved by dimension. Used by the
 * generic expanded header and by the non-stage collapsed rails. A stage is
 * HOUSED in the neutral StageGroupChip, the list band's grammar, in both
 * states: the expanded header (StageColumnHeader) wears the band chip, and
 * the collapsed rail (renderCollapsedColumn) stands the same chip on end, so
 * collapsing a stage column rotates its housing instead of dropping it.
 *
 * Header type spec (Spencer 2026-09-01) for the other dimensions: 14px
 * medium text, every visual at 16px, and the default size-derived 8px
 * visual↔text gap — the same rhythm as the list view's group headers, so
 * switching views doesn't reshuffle the header type. Avatars take the 2xs
 * 16px circle; workflow/team icon marks keep their 16px glyph flush in a
 * 16px rail (`visualBoxSize`) instead of the 20px footprint, which read as
 * extra leading space.
 */
function columnEntityNode(
  column: BoardColumn,
  display: "full" | "visual",
): React.ReactNode {
  if (column.isUnassigned) {
    return (
      <Text size="sm" weight="medium" variant="muted" truncate={1}>
        {column.label}
      </Text>
    );
  }
  switch (column.dimension) {
    case "stage": {
      const stage = column.data as Stage;
      return <StageGroupChip stage={stage} />;
    }
    case "workflow": {
      const workflow = column.data as Workflow;
      return (
        <EntityTag
          entity={{
            kind: "workflow",
            id: workflow.workflowId,
            workflow: { name: workflow.name },
          }}
          display={display}
          visualBoxSize={16}
          interactive={false}
        />
      );
    }
    case "assignee": {
      const user = column.data as UserItem;
      return (
        <EntityTag
          entity={{
            kind: "user",
            id: user.id,
            user: { name: user.title, profileImage: user.avatar?.src ?? null },
          }}
          display={display}
          visualSize="2xs"
          interactive={false}
        />
      );
    }
    case "team":
    case "teams": {
      const team = column.data as TeamItem;
      return (
        <EntityTag
          entity={{
            kind: "team",
            id: team.id,
            team: {
              teamName: team.title,
              icon: {
                icon: iconSourceToName(team.avatar?.icon),
                color: team.avatar?.color ?? null,
              },
            },
          }}
          display={display}
          visualBoxSize={16}
          interactive={false}
        />
      );
    }
    case "age": {
      // The unassigned column is the updates-off bucket (null status on the
      // wire), so it wears the ghost's muted pulse — the same mark the cards
      // under it carry in their footer slot.
      const info = column.isUnassigned
        ? AGE_STATUS_CONFIG.disabled
        : AGE_STATUS_CONFIG[column.key as AgeStatus];
      if (!info) {
        return (
          <Text size="sm" weight="medium" variant="muted" truncate={1}>
            {column.label}
          </Text>
        );
      }
      return (
        <Box direction="row" align="center" gap="sm" overflow="hidden">
          {/* `decoColor` — the same palette hue the card chip and the filter
              chips paint, so a column header and the cards under it read as
              one status. */}
          <Icon name={info.icon} size="md" color={info.decoColor} />
          <Text size="sm" weight="medium" truncate={1}>
            {info.label}
          </Text>
        </Box>
      );
    }
  }
}

/**
 * One board column: mounts its own windowed `Issues` query for the column's
 * grouping selector and hands the page to the library `KanbanColumn`. Rendered
 * once per column via the board's `renderColumn` seam, so each column's hook is
 * its own component instance (hook rules hold). Dimension-neutral — the selector
 * comes from `buildGroupWhere`.
 */
function BoardColumnView({
  column,
  base,
  onlyCurrentUser,
  orderBy,
}: {
  column: BoardColumn;
  base: Record<string, unknown> | undefined;
  onlyCurrentUser: boolean;
  /** Stable reference — a new object re-subscribes every column's query. */
  orderBy: Record<string, unknown>;
}) {
  const where = useMemo(() => {
    const selector = buildGroupWhere(column.dimension, column.key);
    return combineWhereConditions(base ? [selector, base] : [selector]) ?? selector;
  }, [base, column.dimension, column.key]);

  const { issues, dataOffset, total, loading, fetchPage, variablesKey } = useColumnIssues({
    where,
    onlyCurrentUser,
    orderBy,
  });

  return (
    <KanbanColumn<IssueRow, BoardColumn>
      column={column}
      data={{
        items: issues,
        dataOffset,
        total,
        loading,
        fetchPage,
        variablesKey,
        pageSize: COLUMN_PAGE_SIZE,
      }}
    />
  );
}


export function IssuesBoard({ scope }: { scope: IssueScope }) {
  const { track } = useAnalytics();
  const workflowId = scopeWorkflowId(scope);
  // Board realtime: registers the workflow's kanban subscriptions with the
  // shared registry while the board is mounted (inert for cross-workflow
  // scopes, which render read-only).
  useRegisterSubscriptions(
    workflowId ? kanbanBoardSubscriptions(workflowId) : [],
  );
  const {
    filters,
    where: screenFilterWhere,
    activeFilterCount: screenFilterCount,
    viewNoteCount,
    quickLayerFor,
    clearAll: clearAllFilters,
    config: filterConfig,
    filterPanelOpen,
    setFilterPanelOpen,
  } = useIssueFilters();

  // Every display knob on this board — grouping, archived, empty columns, the
  // card layout, Only me — is one `ViewConfig`. Inside a workflow's saved-view
  // scope it is the view's config and the panel's Save commits it; on the team
  // and member boards, which have no view row, it is session state.
  const boardDefaults = useMemo(
    () => ({ groupBy: defaultBoardGrouping(scope) }),
    [scope],
  );
  const { config, setConfig } = useViewConfigState("board", boardDefaults);
  const views = useViewsOptional();
  const activeViewId = views?.activeView?.id ?? null;

  // The toolbar's Only Me is a SESSION overlay on the saved position: a
  // passing "just my work?" press is not an edit to a shared view, so it
  // never drafts. The panel's switch is the saved path — it writes the draft,
  // which previews here because any move of the config value (a panel edit,
  // a save, a discard, a view switch) clears the overlay.
  const [onlyMeSession, setOnlyMeSession] = useState<boolean | null>(null);
  useResetOnChange(`${activeViewId ?? ""}|${String(config.onlyMe)}`, () =>
    setOnlyMeSession(null),
  );
  const {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    isDesktop,
    handleCurrentUserPress,
    handleCurrentUserClear,
  } = useCurrentUserFilterToggle({
    selected: onlyMeSession ?? config.onlyMe,
    onSelectedChange: setOnlyMeSession,
  });


  const [searchValue, setSearchValue] = useState("");
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Issue,
    event: IssueEvent.Search,
    data: { view: AnalyticsView.Board, scope: scope.kind },
  }));

  // A team's board widens to the Projects shared with the team on the view's
  // "Show shared Projects" switch; every other scope is what it is.
  const viewScope = useMemo<IssueScope>(
    () => (scope.kind === "team" ? { ...scope, includeShared: config.showShared } : scope),
    [scope, config.showShared],
  );
  const {
    groupBy,
    groupByLabels,
    groupByIcons,
    groupSortDirection,
    groupByPanel,
    sortBy,
    sortActive,
    columnOrderBy,
    sortByPanel,
    setSortBy,
    setGroupBy,
  } = useIssuesBoardGrouping({ scope: viewScope, workflowId, config, setConfig });


  const searchWhere = useMemo(
    () => buildSearchWhere(searchValue, SEARCH_FIELDS),
    [searchValue],
  );

  // The base filter shared by every column: scope + archived scoping (so the
  // non-stage groupings, whose selectors aren't implicitly scope-bound, don't
  // match issues outside the scope) plus the screen's filters (the view's
  // own layer ANDed with the toolbar's temporary pool) + search.
  // `onlyCurrentUser` is applied per column by `useColumnIssues`, not folded here.
  // The completed / canceled toggles narrow by naming the stages the view
  // keeps, out of the whole workspace's — the scope predicate already pins
  // the workflow, and a cross-workflow board keeps every open stage.
  const { stages: workspaceStages } = useWorkspaceData();
  const stageWhere = useMemo(
    () => viewStageWhere(config, workspaceStages),
    [config, workspaceStages],
  );
  const base = useMemo(() => {
    const conditions: Record<string, unknown>[] = [scopeWhere(viewScope)];
    // "Show archived" widens the board rather than narrowing it: off, the
    // archived rows are excluded; on, nothing filters on `archived` at all.
    const archivedWhere = viewArchivedWhere({ showArchived: config.showArchived }, screenFilterWhere);
    if (archivedWhere) conditions.push(archivedWhere);
    if (stageWhere) conditions.push(stageWhere);
    if (screenFilterWhere) conditions.push(screenFilterWhere);
    if (searchWhere) conditions.push(searchWhere);
    return combineWhereConditions(conditions) ?? undefined;
  }, [viewScope, config.showArchived, stageWhere, screenFilterWhere, searchWhere]);

  const { stages, loading } = useWorkflowStagesData(workflowId);
  const { workflows } = useAppData();

  const {
    needsCounts,
    countsSettled,
    columns,
    collapsedColumnIds,
    handleToggleColumnCollapse,
    handleDropIntoCollapsedColumn,
  } = useIssuesBoardColumns({
    groupBy,
    groupSortDirection,
    config,
    setConfig,
    base,
    isCurrentUserFilterApplied,
    stages: stages as Stage[],
    workflows,
  });

  // --- Bulk selection ---
  // Flat id set + accumulated row map (columns page and evict). Cleared when
  // the board's identity changes — scope or grouping — AND when filters or
  // search change: a group drag lands every selected card, so a selection
  // that survived a filter would silently move cards the filter now hides.
  const { selectedIds, selectedRowData, toggle, refresh, clear } = useBoardSelection();
  useResetOnChange(
    `${scopeKey(scope)}::${groupBy}::${JSON.stringify(screenFilterWhere ?? null)}::${searchValue}`,
    clear,
  );


  const { handleDragEnd, moveIssueToStage } = useIssuesBoardDrag({
    groupBy,
    sortActive,
    selectedRowData,
  });

  const { openCreateIssue, openIssue } = useIssueView();

  // Custom-field defs for every card's field tokens — ONE query at the board
  // level (never per card; same lift as the table's columns).
  const customFieldDefs = useAllCustomFields();

  // The card's own display knobs, a member of the view's config: the panel's
  // Fields page edits them, and cards see only the already-filtered field defs
  // plus the show/hide + mode flags.
  const displayOptions = config.card;
  const trackOption = useTrackViewOption();
  const updateDisplayOptions = useCallback(
    (patch: Partial<CardDisplayOptions>) => {
      setConfig({ card: { ...config.card, ...patch } });
    },
    [config.card, setConfig],
  );
  // Custom fields are OPT-IN on cards (inclusion list): none show until the
  // user picks them in the popover, and newly created fields stay off
  // everyone's cards.
  const visibleFieldDefs = useMemo(
    () => getShownCardFieldDefs(customFieldDefs, displayOptions.shownFieldIds),
    [customFieldDefs, displayOptions.shownFieldIds],
  );

  // Card + 8px row gap. Title-only: border 2 + rail 40 + separator 1 + footer
  // (8 + 24) + bottom pad 8 = 83, +8 gap = 91. Card BODY objects (the address
  // chip, the extra property chips, field tokens) run a 28px floor on a 4px
  // object rhythm; the first body object rides the rail's whitespace, and any
  // body content adds the separator's 16px stand-off. Custom fields render as
  // 28px TOKENS (one per field) — wrap layout estimates ~2 per line.
  // Estimates only — measureElement corrects.
  const estimateItemSize = useCallback(
    (i: IssueRow | undefined) => {
      const hasAddr = displayOptions.showAddress && !!i?.address?.wholeAddress;
      const extraRows =
        (displayOptions.showStage && !!i?.stageId ? 1 : 0) +
        (displayOptions.showWorkflow && !!i?.workflowId ? 1 : 0) +
        (displayOptions.showProjectType && !!i?.issueType ? 1 : 0) +
        (displayOptions.showCreated && !!i?.creationDate ? 1 : 0) +
        (displayOptions.showUpdated && !!i?.lastUpdateDate ? 1 : 0);
      const bodyRows = (hasAddr ? 1 : 0) + extraRows;
      const tokens = i
        ? countCardFieldTokens(
            getCardCustomFields(
              visibleFieldDefs,
              i.values,
              displayOptions.showEmptyFields,
            ),
          )
        : 0;
      const tokenLines =
        displayOptions.fieldLayout === "list" ? tokens : Math.ceil(tokens / 2);
      let extra = bodyRows * 28 + Math.max(0, bodyRows - 1) * 4;
      extra +=
        tokenLines * 28 +
        Math.max(0, tokenLines - 1) * 4 +
        (tokenLines > 0 && bodyRows > 0 ? 4 : 0);
      if (bodyRows + tokenLines > 0) extra += 16;
      return 91 + extra;
    },
    [
      visibleFieldDefs,
      displayOptions.showAddress,
      displayOptions.showStage,
      displayOptions.showWorkflow,
      displayOptions.showProjectType,
      displayOptions.showCreated,
      displayOptions.showUpdated,
      displayOptions.fieldLayout,
      displayOptions.showEmptyFields,
    ],
  );

  // Stage changes from the cards' property-chip selectors — board-level.
  // Stage MUST go through the canonical kanban move (same as drag and the
  // list) — its cache update moves the card between the stage columns'
  // connections. A plain updateIssue(stageId) only rewrites the normalized
  // entity, so on a stage-grouped board the card would sit in the wrong
  // column until a refetch. End-of-column placement (the list's grammar:
  // sortOrder MAX_SAFE_INTEGER).
  const handleStageChange = useCallback(
    async (issue: IssueRow, stageId: string) => {
      if (stageId === issue.stageId) return;
      try {
        await moveIssueToStage({
          issue,
          fromStageId: issue.stageId,
          toStageId: stageId,
          newSortOrder: Number.MAX_SAFE_INTEGER,
        });
      } catch (error) {
        toastMutationError(error, "Failed to update stage");
      }
    },
    [moveIssueToStage],
  );

  const issueCardActions = useIssueCardEditActions({
    changeStage: handleStageChange,
    view: AnalyticsView.Board,
  });

  // Inline rename — the card menu's Rename puts that card's title into edit
  // mode in place (see issue-rename-context.tsx; the table provides the same
  // controls over its name cells).
  const renameControls = useIssueRenameControls();

  // Create gate — both the toolbar "Add Project" and the per-column footer require
  // role `create.items` AND edit-or-better access on this workflow. Only a
  // single-workflow scope can create (you must know which workflow to add to).
  const workflow = workflows.find((w) => w.workflowId === workflowId);
  const canCreateItems = usePermission("create.items");
  const canAddProject =
    !!workflowId && canCreateItems && canEdit(workflow?.viewerAccessLevel);

  // "Automate stage" on a stage group — shared gate.
  const { canAutomate: canAutomateStage } = useAutomateFromView(workflowId);

  // Inline stage rename/recolor from the column header — the same actions the
  // list's stage group headers offer, mounted ONCE here, not per column.
  const {
    canEditStages: canEditStage,
    renameStage: handleRenameStage,
    recolorStage: handleRecolorStage,
  } = useStageEditActions("board_column");
  const { openStagesDialog } = useStagesDialogActions();

  const handleAddProject = useCallback(
    () => workflowId && openCreateIssue({ workflowId, source: "kanban_header" }),
    [workflowId, openCreateIssue],
  );

  // On a touch device the board is static: a drag across columns that each
  // fill the screen is a fight with the scroll, so a card moves by its ⋯
  // menu's Stage instead (`touchMenu`), and a hold does nothing.
  const isTouch = useIsTouchDevice();

  const renderCard = useCallback(
    (
      issue: IssueRow,
      {
        isDragOverlay,
        isSelected,
      }: { isDragOverlay: boolean; isSelected: boolean },
    ) => (
      <>
        {isSelected ? <SelectedRowSync issue={issue} refresh={refresh} /> : null}
        <IssueCard
          issue={issue}
          isDragOverlay={isDragOverlay}
          isSelected={isSelected}
          touchMenu
        />
      </>
    ),
    [refresh],
  );

  // The card cluster's actions: the hover Rename pencil (same in-place rename
  // the list's name cells offer — it renders nothing on an issue the viewer
  // cannot edit), then the 'More' slot — the same menu the card already offers
  // on right-click, in its dropdown flavor (it renders its own trigger button).
  const cardActions = useCallback(
    (issue: IssueRow) => (
      <>
        <IssueRenameAction issue={issue} variant="ghost" />
        <IssueMenu
          issue={issue}
          type="dropdown"
          triggerShape="pill"
          readOnly={!issue.viewerCanEdit}
          triggerTooltip="Project actions"
          triggerTooltipSide="top"
        />
      </>
    ),
    [],
  );

  const handleCardPress = useCallback(
    (issue: IssueRow) => openIssue(issue, { source: "card" }),
    [openIssue],
  );

  // --- The column ⋯ menu's "Follow up cycle…" target ---
  // ONE menu at board level, anchored to the open column's ⋯ button (each
  // column hands its own out through `renderColumnHeader`), so it drops in the
  // identical spot as the menu it was picked from. The id is the state, not the
  // Stage: the pin is saved through this menu, and a Stage snapshot taken at
  // click time would show the pre-save cycle for the rest of the session.
  const [cycleStageId, setCycleStageId] = useState<string | null>(null);
  const columnMenuTriggerRefs = useRef(
    new Map<string, RefObject<Element | null>>(),
  );
  const cycleStage = useMemo(
    () => stages.find((s) => s.stageId === cycleStageId) ?? null,
    [stages, cycleStageId],
  );
  // A VIRTUAL anchor, not a ref: Base UI reads a ref's `.current` once when it
  // registers the reference, so a rect that is not final at that moment is the
  // one the popup opens against — it paints in the viewport's corner and then
  // travels to the column. `getBoundingClientRect` is called on every
  // positioning pass instead, which is also what keeps the popup on the ⋯
  // through board scroll. `contextElement` is the real element behind the rect,
  // so anchor tracking still observes the right scroll ancestors.
  const cycleAnchor = useMemo<MenuAnchor>(() => {
    const resolve = () =>
      cycleStageId
        ? (columnMenuTriggerRefs.current.get(cycleStageId)?.current ?? null)
        : null;
    return {
      get contextElement() {
        return resolve() ?? undefined;
      },
      getBoundingClientRect: () =>
        resolve()?.getBoundingClientRect() ?? new DOMRect(),
    };
  }, [cycleStageId]);

  // Ids-only: the board has no select-all, so no `getGroupWhere`.
  const refetchGroups = useRefetchIssueGroups();
  const bulkActions = useIssueBulkActions({
    view: AnalyticsView.Board,
    scope: scope.kind,
    workflowId,
    availableStages: stages as Stage[],
    refetchGroups,
  });

  const renderColumn = useCallback(
    (column: BoardColumn) => (
      <BoardColumnView
        column={column}
        base={base}
        onlyCurrentUser={isCurrentUserFilterApplied}
        orderBy={columnOrderBy}
      />
    ),
    [base, isCurrentUserFilterApplied, columnOrderBy],
  );

  const renderColumnHeader = useCallback(
    ({
      column,
      count,
      menuTriggerRef,
      setControlsHidden,
    }: {
      column: BoardColumn;
      count: number;
      menuTriggerRef: RefObject<Element | null>;
      setControlsHidden: (hidden: boolean) => void;
    }) => {
      // Record this column's ⋯ so the board-level cycle menu can hang off
      // whichever one the user opened the menu from. Keyed by the dimension
      // key — the Stage id while grouping by stage, which is what the popover
      // holds. Idempotent: the ref object is the column's own and stable for
      // its lifetime.
      columnMenuTriggerRefs.current.set(column.key, menuTriggerRef);
      // A stage column's header is editable in place; every other dimension
      // groups by something this board does not own, so it reads only.
      if (column.dimension === "stage" && !column.isUnassigned) {
        const stage = column.data as Stage;
        return (
          <StageColumnHeader
            stage={stage}
            count={count}
            canEditStage={canEditStage}
            onRename={handleRenameStage}
            onRecolor={handleRecolorStage}
            onOpenSettings={
              workflowId
                ? () => openStagesDialog(workflowId)
                : undefined
            }
            setControlsHidden={setControlsHidden}
          />
        );
      }
      return (
        // 8px chip↔count rhythm, the same across every grouping dimension so
        // switching the dimension doesn't reshuffle the header layout. The
        // 8px lead-in matches StageColumnHeader: with the header's own 8px
        // inset it puts the glyph 16px in — the "+ Add Project" footer's
        // glyph line, so header glyph, footer plus and their labels stack.
        <Box direction="row" align="center" gap="sm" paddingLeft="sm" shrink>
          {columnEntityNode(column, "full")}
          <Text size="sm" weight="medium" variant="muted">
            {count.toLocaleString()}
          </Text>
        </Box>
      );
    },
    [canEditStage, handleRenameStage, handleRecolorStage, workflowId, openStagesDialog],
  );

  const renderCollapsedColumn = useCallback(
    ({ column, count }: { column: BoardColumn; count: number }) => (
      <Box direction="column" align="center" gap="sm">
        {column.dimension === "stage" && !column.isUnassigned ? (
          // The rail IS the header rotated: the stage keeps its housed chip,
          // stood on end, 24px wide like the expanded header's control row.
          <StageGroupChip stage={column.data as Stage} orientation="vertical" />
        ) : (
          <>
            {/* The glyph sits in a 24px slot mirroring the expanded header's
                control row, so it lines up with neighboring expanded headers.
                The unassigned bucket has no glyph. */}
            {!column.isUnassigned ? (
              <Box height={24} align="center" justify="center" shrink={false}>
                {columnEntityNode(column, "visual")}
              </Box>
            ) : null}
            {/* Same type ramp as the expanded header — collapsing only rotates
                the header, it doesn't shrink it. The vertical rail label is a
                class pair (no Box writing-mode prop). */}
            <Box className="[writing-mode:vertical-rl] whitespace-nowrap">
              {/* Same 14px-medium rung as the expanded header (the rail is the
                  header rotated); unassigned keeps only its muted ink. */}
              <Text
                size="sm"
                weight="medium"
                variant={column.isUnassigned ? "muted" : undefined}
              >
                {column.label}
              </Text>
            </Box>
          </>
        )}
        <Box className="[writing-mode:vertical-rl]">
          <Text size="sm" weight="medium" variant="muted">
            {count.toLocaleString()}
          </Text>
        </Box>
      </Box>
    ),
    [],
  );

  // Stage-only chrome: the header "+", the per-column "Add Project" footer and
  // the "Edit stage" overflow item only make sense when columns ARE stages.
  const renderColumnHeaderActions = useMemo(
    () =>
      groupBy === "stage" && canAddProject
        ? (column: BoardColumn) =>
            column.isUnassigned ? null : (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      // The footer strip below says "Add Project" too; this
                      // name tells the two apart, and says the column.
                      aria-label={`Add Project to ${column.label}`}
                      onClick={() =>
                        workflowId &&
                        openCreateIssue({
                          workflowId,
                          stageId: column.key,
                          source: "kanban_column_header",
                        })
                      }
                    />
                  }
                >
                  <Icon name="plus" size="md" />
                </TooltipTrigger>
                <TooltipPopup side="top">Add Project</TooltipPopup>
              </Tooltip>
            )
        : undefined,
    [groupBy, canAddProject, workflowId, openCreateIssue],
  );

  const renderColumnFooter = useMemo(
    () =>
      groupBy === "stage" && canAddProject
        ? (column: BoardColumn) => (
            <KanbanAddCard
              label="Add Project"
              onClick={() =>
                workflowId &&
                openCreateIssue({
                  workflowId,
                  stageId: column.key,
                  source: "kanban_column",
                })
              }
            />
          )
        : undefined,
    [groupBy, canAddProject, workflowId, openCreateIssue],
  );

  const columnMenuItems = useMemo(
    () =>
      groupBy === "stage"
        ? (column: BoardColumn) => (
            <>
              {canEditStage ? (
                <MenuItem
                  onClick={() => workflowId && openStagesDialog(workflowId)}
                >
                  <Icon name="settings" size="md" />
                  {/* Plural — the item opens the workflow's stage manager
                      dialog (create/rename/reorder/delete, drafted + applied). */}
                  <Text>Edit stages</Text>
                </MenuItem>
              ) : null}
              {/* Closed Stages get the item too — they default off, but pinning
                  a cycle opts finished work into updates. */}
              <MenuItem onClick={() => setCycleStageId(column.key)}>
                <Icon name={PROPERTY_ICON.followUp} size="md" />
                <Text>Follow up cycle…</Text>
              </MenuItem>
              {canAutomateStage && workflowId ? (
                <AutomateStageMenuItem
                  workflowId={workflowId}
                  stageId={column.key}
                />
              ) : null}
            </>
          )
        : undefined,
    [groupBy, workflowId, canEditStage, openStagesDialog, canAutomateStage],
  );

  // Stage drags move/reorder; assignee and teams drags reassign. Workflow
  // needs a stage mapping and follow-up is derived, so those stay read-only.
  const isDropDisabled =
    groupBy !== "stage" && groupBy !== "assignee" && groupBy !== "teams";
  const dropDisabledOverlay = useMemo(
    () => (
      <Box
        align="center"
        gap="xs"
        padding="md"
        maxWidth={220}
      >
        <Icon name="lock" size="md" color="muted" />
        <Text size="xs" variant="muted" align="center">
          Group by Stage, Assignee, or Teams to move Projects
        </Text>
      </Box>
    ),
    [],
  );

  const hasActiveFilters = screenFilterCount > 0;

  if ((loading && stages.length === 0) || (needsCounts && !countsSettled)) {
    return (
      <Box fill="flex" direction="row" gap="md" padding="md">
        {Array.from({ length: 4 }).map((_, i) => (
          <KanbanColumnSkeleton key={i} />
        ))}
      </Box>
    );
  }

  return (
    <IssueRenameProvider value={renameControls}>
    <IssueCardProvider
      actions={issueCardActions}
      customFieldDefs={visibleFieldDefs}
      displayOptions={displayOptions}
    >
      {/* `relative` anchors the floating bulk-actions bar. */}
      <Box direction="column" fill="flex" position="relative">
      {/* The view's toolbar band, below the tab strip. Page-default
          background, no bottom hairline — it sits open over the board. */}
      <Box background="background">
        <DataTableToolbar>
          <DataTableToolbarLeft>
            <DataTableGroupByDropdown
              groupByField={groupBy}
              setGroupByField={setGroupBy}
              groupByLabels={groupByLabels}
              groupByIcons={groupByIcons}
              groupSortDirection={groupSortDirection}
              setGroupSortDirection={(direction) =>
                setConfig({ groupDirection: direction })
              }
              requireGrouping={groupByPanel.requireGrouping}
            />
          </DataTableToolbarLeft>
          <DataTableToolbarActions>
            {/* First in the cluster: unsaved edits are the thing to
                answer before anything else in the row. Absent while
                the view matches what is saved. */}
            <ViewSaveButton />
            {/* Card order within every column — icon-only while unsorted,
                the active field's chip while sorting. Ahead of the filter
                cluster: sorting REORDERS the board where the cluster
                NARROWS it. */}
            <DataTableSortByDropdown
              sortByField={sortBy}
              setSortByField={setSortBy}
              sortByLabels={ISSUE_SORT_LABELS}
              sortByIcons={ISSUE_SORT_ICONS}
              sortDirection={config.sortDirection}
              setSortDirection={(direction) =>
                setConfig({ sortDirection: direction })
              }
            />
            {/* Filter cluster in the shared cross-view order: the quick-filter
                chip, then Only-Me, then search. */}
            {/* The panel renders the chip and hangs off it — a popover
                anchored to the control that opened it belongs beside that
                control, not up in the route. */}
            <FilterPanel
              open={filterPanelOpen}
              onOpenChange={setFilterPanelOpen}
              config={filterConfig}
              filters={filters}
              scope={scope}
              viewFilters={
                views && viewNoteCount > 0
                  ? {
                      count: viewNoteCount,
                      onEdit: () => views.openPanel("filters", "filter_chip"),
                    }
                  : undefined
              }
              onClearAll={clearAllFilters}
              layerFor={quickLayerFor}
              trigger={
                // The chip counts BOTH layers — how narrowed the screen is —
                // and its × clears both; a view filter cleared here is a
                // view edit, so Save appears for it.
                <FilterToggle
                  size="xs"
                  icon="filter"
                  count={screenFilterCount}
                  selected={hasActiveFilters}
                  onClear={() => clearAllFilters("chip")}
                  tooltip="Quickly filter your Projects"
                  clearTooltip="Clear all filters"
                />
              }
            />
            {/* The Assignee filter's own door — the same value the panel's
                Assignee row edits, one press from Unassigned. */}
            <AssigneeFilterChip
              config={filterConfig}
              filters={quickLayerFor(ASSIGNEE_PRINCIPAL_FIELD)}
              scope={scope}
              size="xs"
              tooltip="Filter Projects by assignee"
            />
            <CurrentUserButton
              selected={isCurrentUserButtonSelected}
              onClick={() => {
                const enabled = isDesktop ? true : !isCurrentUserButtonSelected;
                handleCurrentUserPress();
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ToggleOnlyMe,
                  data: { view: AnalyticsView.Board, scope: scope.kind, enabled },
                });
              }}
              onClearClick={() => {
                handleCurrentUserClear();
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ToggleOnlyMe,
                  data: { view: AnalyticsView.Board, scope: scope.kind, enabled: false },
                });
              }}
              size="xs"
            />
            <ViewSearch
              value={searchValue}
              onValueChange={setSearchValue}
              size="xs"
            />
            {/* The trailing group past the separator: the view's own controls
                (display options beside the create action), set apart from the
                filter cluster — the same grammar the list toolbar uses. */}
            <Box direction="row" align="center" gap="sm">
              <Separator orientation="vertical" className="h-4" />
              <ViewOptionsTrigger />
              {canAddProject && (
                <ToolbarAction
                  // Text-only at full width ("Add Project", no plus); the
                  // glyph only appears when the action collapses to a bare
                  // "+" at compact widths, where the label would push the
                  // toolbar row past the screen's edge.
                  collapsedIcon="plus"
                  label="Add Project"
                  aria-label="Add Project"
                  tooltip="Add a Project to this location"
                  onClick={handleAddProject}
                />
              )}
            </Box>
          </DataTableToolbarActions>
        </DataTableToolbar>
      </Box>
      {/* The view's panel belongs to the body, not the route: only the body
          knows which grouping dimensions and which Fields editor it offers.
          Outside a saved-view scope it renders nothing. */}
      <ViewOptionsPanel
        anchorId={VIEW_PANE_ANCHOR_ID}
        groupBy={groupByPanel}
        sortBy={sortByPanel}
        display={
          <>
            {/* Draft edits, open to every viewer — without `create.views`
                they are temporary. */}
            <CardSizeRow
              value={config.cardSize}
              onChange={(cardSize) => {
                setConfig({ cardSize });
                trackOption("cardSize", { value: cardSize });
              }}
            />
            <CardLayoutRows
              options={displayOptions}
              onChange={(patch) => {
                updateDisplayOptions(patch);
                for (const [key, value] of Object.entries(patch)) {
                  trackOption(key, typeof value === "boolean" ? { on: value } : { value });
                }
              }}
              switches={<CollapseEmptyStagesRow />}
            />
          </>
        }
        fields={{
          editor: (
            <CardDisplayFields
              options={displayOptions}
              onChange={updateDisplayOptions}
              fields={customFieldDefs}
            />
          ),
          // The main page's Fields row readout — the Fields page's own Shown
          // count, the pinned title aside.
          shownCount: countShownCardFields(displayOptions, customFieldDefs),
        }}
      />
      <KanbanBoard<IssueRow, BoardColumn>
        columns={columns}
        getColumnId={getColumnId}
        getColumnLabel={getColumnLabel}
        getItemId={getItemId}
        getItemLabel={getItemLabel}
        getSortValue={getSortValue}
        renderColumn={renderColumn}
        estimateItemSize={estimateItemSize}
        isItemDragDisabled={isItemDragDisabled}
        isDropDisabled={isDropDisabled}
        dropDisabledOverlay={dropDisabledOverlay}
        draggable={!isTouch}
        onDragEnd={handleDragEnd}
        renderCard={renderCard}
        onCardClick={handleCardPress}
        renderColumnHeader={renderColumnHeader}
        renderColumnHeaderActions={renderColumnHeaderActions}
        renderCollapsedColumn={renderCollapsedColumn}
        renderColumnFooter={renderColumnFooter}
        columnMenuItems={columnMenuItems}
        selectedItemIds={selectedIds}
        onItemSelectedChange={toggle}
        collapsedColumnIds={collapsedColumnIds}
        onToggleColumnCollapse={handleToggleColumnCollapse}
        onDropIntoCollapsedColumn={handleDropIntoCollapsedColumn}
        columnWidth={BOARD_CARD_SIZE_WIDTHS[config.cardSize]}
        isItemSelectable={isItemSelectable}
        cardActions={cardActions}
        dragGroupLabel={dragGroupLabel}
        emptyState={<EmptyState size="xs" title="No columns to show" />}
      />
      <StageUpdateCycleMenu
        open={cycleStage != null}
        onOpenChange={(open) => {
          if (!open) setCycleStageId(null);
        }}
        stage={cycleStage}
        anchor={cycleAnchor}
      />
      <BulkActionsBar
        actions={bulkActions}
        selectionNoun={PROJECT_SELECTION_NOUN}
        selectedIds={selectedIds}
        onClearSelection={clear}
        selectedRowData={selectedRowData}
        selectAllGroups={NO_SELECT_ALL_GROUPS}
      />
      </Box>
    </IssueCardProvider>
    </IssueRenameProvider>
  );
}
